import { useId, useState, type ReactElement } from "react";
import { ChartLegend, EmptyState, StatusDot, cn } from "@playbookai/ui";
import type { AdoptionPeriodDays } from "../../../api/admin-adoption-api.js";
import { seriesDot, seriesStyle } from "./series-style.js";
import { formatCount } from "./adoption-view.js";
import type { AdoptionModuleView } from "./adoption-view.js";

interface ChartPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * Shape-preserving monotone cubic interpolation. Daily counts remain the source
 * points, while the tangent limiter avoids the negative dips and exaggerated
 * overshoot a generic spline can invent around sparse event spikes.
 */
function monotonePath(points: readonly ChartPoint[]): string {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0]?.x},${points[0]?.y}`;

  const slopes = points.slice(1).map((point, index) => {
    const previous = points[index] as ChartPoint;
    return (point.y - previous.y) / (point.x - previous.x);
  });
  const tangents = points.map((point, index) => {
    if (index === 0) return slopes[0] ?? 0;
    if (index === points.length - 1) return slopes.at(-1) ?? 0;
    const before = slopes[index - 1] ?? 0;
    const after = slopes[index] ?? 0;
    if (before === 0 || after === 0 || Math.sign(before) !== Math.sign(after)) {
      return 0;
    }
    const previous = points[index - 1] as ChartPoint;
    const next = points[index + 1] as ChartPoint;
    const beforeWidth = point.x - previous.x;
    const afterWidth = next.x - point.x;
    const firstWeight = 2 * afterWidth + beforeWidth;
    const secondWeight = afterWidth + 2 * beforeWidth;
    return (
      (firstWeight + secondWeight) /
      (firstWeight / before + secondWeight / after)
    );
  });

  return points.slice(1).reduce(
    (path, point, index) => {
      const previous = points[index] as ChartPoint;
      const width = point.x - previous.x;
      const firstControl = {
        x: previous.x + width / 3,
        y: previous.y + ((tangents[index] ?? 0) * width) / 3,
      };
      const secondControl = {
        x: point.x - width / 3,
        y: point.y - ((tangents[index + 1] ?? 0) * width) / 3,
      };
      return `${path} C ${firstControl.x.toFixed(1)},${firstControl.y.toFixed(1)} ${secondControl.x.toFixed(1)},${secondControl.y.toFixed(1)} ${point.x.toFixed(1)},${point.y.toFixed(1)}`;
    },
    `M ${points[0]?.x.toFixed(1)},${points[0]?.y.toFixed(1)}`,
  );
}

export function AdoptionChart({
  readings,
  days,
  formatDay,
}: {
  readings: readonly AdoptionModuleView[];
  days: AdoptionPeriodDays;
  formatDay: (day: string) => string;
}): ReactElement {
  const [activeDate, setActiveDate] = useState<string | null>(null);
  const tooltipId = useId();
  const titleId = useId();
  const descriptionId = useId();
  const ready = readings.filter(
    (reading) =>
      reading.status === "ready" &&
      reading.availability !== "never-enabled" &&
      reading.points.length > 0,
  );
  const values = ready.flatMap((reading) =>
    reading.points.map((point) => point.count),
  );
  const max = Math.max(1, ...values);
  const width = 960;
  const height = 260;
  const padding = { top: 18, right: 18, bottom: 30, left: 38 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const pointFor = (
    count: number,
    index: number,
    length: number,
  ): ChartPoint => {
    const x = padding.left + (index / Math.max(1, length - 1)) * plotWidth;
    const y = padding.top + plotHeight - (count / max) * plotHeight;
    return { x, y };
  };
  const dates = [
    ...new Set(
      ready.flatMap((reading) => reading.points.map((point) => point.date)),
    ),
  ].sort();
  const labelIndexes = [
    ...new Set([0, Math.floor((dates.length - 1) / 2), dates.length - 1]),
  ].filter((index) => index >= 0);
  const activeIndex = activeDate === null ? -1 : dates.indexOf(activeDate);
  const activeX = pointFor(0, Math.max(0, activeIndex), dates.length).x;
  if (ready.length === 0) {
    return (
      <EmptyState
        title="Adoption data is unavailable"
        description="No enabled module returned an adoption reading. Try again in a moment."
      />
    );
  }

  return (
    <>
      <ChartLegend
        className="mb-4"
        label="Chart legend"
        items={readings.map((reading) => ({
          id: reading.id,
          marker: "dot" as const,
          ...seriesDot(reading.id),
          label: `${reading.label}${
            reading.availability === "never-enabled"
              ? " (not enabled)"
              : reading.status === "unavailable"
                ? " (unavailable)"
                : reading.availability === "disabled"
                  ? " (disabled now)"
                  : ""
          }`,
        }))}
      />
      <div className="overflow-x-auto">
        <div
          className="relative min-w-[640px]"
          onPointerLeave={() => setActiveDate(null)}
        >
          {activeIndex >= 0 && activeDate ? (
            <div
              id={tooltipId}
              role="tooltip"
              className="absolute top-1 z-popover w-64 rounded-lg border border-pb-border bg-pb-card-elevated p-3 text-xs text-pb-text shadow-[var(--pbds-shadow-float)]"
              style={{
                left: `clamp(0px, calc(${(activeX / width) * 100}% - 8rem), calc(100% - 16rem))`,
              }}
            >
              <p className="mb-2 font-semibold">{formatDay(activeDate)}</p>
              <dl className="space-y-1.5">
                {readings.map((reading) => {
                  const point = reading.points.find(
                    (value) => value.date === activeDate,
                  );
                  const value =
                    reading.availability === "never-enabled"
                      ? "Not enabled"
                      : reading.status !== "ready" || !point
                        ? "Unavailable"
                        : formatCount(point.count);
                  return (
                    <div
                      key={reading.id}
                      className="flex items-center justify-between gap-3"
                    >
                      <dt className="flex items-center gap-2">
                        <StatusDot {...seriesDot(reading.id)} />
                        {reading.label}
                      </dt>
                      <dd className="pb-tabular font-medium">{value}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          ) : null}

          <svg
            className="h-auto min-w-[640px] w-full"
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            tabIndex={0}
            aria-describedby={activeIndex >= 0 ? tooltipId : undefined}
            onFocus={() => setActiveDate(dates[0] ?? null)}
            onBlur={() => setActiveDate(null)}
            onPointerMove={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              if (rect.width === 0) return;
              const x = ((event.clientX - rect.left) * width) / rect.width;
              const index = Math.round(
                Math.max(0, Math.min(1, (x - padding.left) / plotWidth)) *
                  (dates.length - 1),
              );
              setActiveDate(dates[index] ?? null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setActiveDate(null);
                return;
              }
              if (
                !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)
              )
                return;
              event.preventDefault();
              const index =
                event.key === "Home"
                  ? 0
                  : event.key === "End"
                    ? dates.length - 1
                    : Math.max(
                        0,
                        Math.min(
                          dates.length - 1,
                          activeIndex + (event.key === "ArrowRight" ? 1 : -1),
                        ),
                      );
              setActiveDate(dates[index] ?? null);
            }}
            aria-labelledby={`${titleId} ${descriptionId}`}
          >
            <title id={titleId}>Daily adoption activity over {days} days</title>
            <desc id={descriptionId}>
              Hover or use Left and Right arrow keys to inspect a day; Escape
              closes the tooltip. Daily counts for each enabled module. A data
              table follows the chart.
            </desc>
            {[0, 0.5, 1].map((ratio) => {
              const y = padding.top + plotHeight - ratio * plotHeight;
              return (
                <g key={ratio}>
                  <line
                    x1={padding.left}
                    x2={width - padding.right}
                    y1={y}
                    y2={y}
                    className="stroke-pb-border/70"
                    strokeDasharray="3 5"
                  />
                  <text
                    x={padding.left - 9}
                    y={y + 4}
                    textAnchor="end"
                    className="fill-pb-text-muted text-dense-1"
                  >
                    {Math.round(max * ratio)}
                  </text>
                </g>
              );
            })}
            {ready
              .filter((reading) =>
                reading.points.some((point) => point.count > 0),
              )
              .map((reading) => {
                const countByDate = new Map(
                  reading.points.map((point) => [point.date, point.count]),
                );
                const points = dates.map((date, index) =>
                  pointFor(countByDate.get(date) ?? 0, index, dates.length),
                );
                return (
                  <g key={reading.id} data-series={reading.id}>
                    <path
                      d={monotonePath(points)}
                      fill="none"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeDasharray={
                        reading.availability === "disabled" ? "6 5" : undefined
                      }
                      className={seriesStyle(reading.id).stroke}
                    />
                    {dates.map((date, index) => {
                      const count = countByDate.get(date) ?? 0;
                      if (count === 0) return null;
                      const position = points[index];
                      if (!position) return null;
                      return (
                        <circle
                          key={date}
                          cx={position.x}
                          cy={position.y}
                          r="2.75"
                          className={cn(
                            seriesStyle(reading.id).fill,
                            "stroke-pb-card-elevated",
                          )}
                          strokeWidth="1.5"
                        />
                      );
                    })}
                  </g>
                );
              })}
            <line
              data-testid="adoption-zero-baseline"
              x1={padding.left}
              x2={width - padding.right}
              y1={padding.top + plotHeight}
              y2={padding.top + plotHeight}
              className="stroke-pb-border-strong"
              strokeWidth="2.5"
            />
            {activeIndex >= 0 ? (
              <line
                x1={activeX}
                x2={activeX}
                y1={padding.top}
                y2={padding.top + plotHeight}
                className="stroke-pb-text-muted"
                strokeDasharray="3 4"
                pointerEvents="none"
              />
            ) : null}
            {labelIndexes.map((index) => {
              const date = dates[index];
              if (!date) return null;
              const x =
                padding.left +
                (index / Math.max(1, dates.length - 1)) * plotWidth;
              return (
                <text
                  key={date}
                  x={x}
                  y={height - 7}
                  textAnchor={
                    index === 0
                      ? "start"
                      : index === dates.length - 1
                        ? "end"
                        : "middle"
                  }
                  className="fill-pb-text-muted text-dense-1"
                >
                  {formatDay(date)}
                </text>
              );
            })}
          </svg>
        </div>
      </div>
      {/* sr-only sits on a wrapper because a table cannot shrink to 1px; the
          relative anchor keeps that 1px box inside the chart, not on the page. */}
      <div className="relative">
        <div className="sr-only">
          {/* eslint-disable-next-line ds/no-hand-built -- kept: the chart's screen-reader data table (sr-only), the text twin of the SVG marks; DataTable would add a visible frame. */}
          <table>
            <caption>Daily adoption counts</caption>
            <thead>
              <tr>
                <th>Date</th>
                {ready.map((reading) => (
                  <th key={reading.id}>{reading.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dates.map((date) => (
                <tr key={date}>
                  <th>{formatDay(date)}</th>
                  {ready.map((reading) => (
                    <td key={reading.id}>
                      {reading.points.find((point) => point.date === date)
                        ?.count ?? 0}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
